import {
  getFooterColumnNames,
  ParquetColumnRename,
  ParquetColumnRenameError,
  renameFooterColumns,
} from './parquetFooter';

export { ParquetColumnRenameError } from './parquetFooter';
export type { ParquetColumnRename } from './parquetFooter';

const MAGIC = 'PAR1';
const ENCRYPTED_MAGIC = 'PARE';
const TRAILER_BYTES = 8;

export interface ParquetByteSource {
  getSize(signal?: AbortSignal): Promise<number>;
  readBytes(start: number, end: number, signal?: AbortSignal): Promise<Uint8Array>;
  readBlob(start: number, end: number, signal?: AbortSignal): Promise<Blob>;
}

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    throw new DOMException('The Parquet rewrite was cancelled', 'AbortError');
  }
};

export const blobToBytes = (blob: Blob): Promise<Uint8Array> => {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
};

export const createBlobParquetSource = (blob: Blob): ParquetByteSource => ({
  getSize: async () => blob.size,
  readBytes: async (start, end, signal) => {
    throwIfAborted(signal);
    return blobToBytes(blob.slice(start, end));
  },
  readBlob: async (start, end, signal) => {
    throwIfAborted(signal);
    return blob.slice(start, end);
  },
});

// The size comes from the HEAD URL's Content-Length, as the viewer's parquet reader does. Content-Range is not
// readable cross-origin unless the bucket's CORS configuration exposes it, so it is not relied on.
export const createRemoteParquetSource = (
  url: string,
  { headUrl, fetch: fetchImplementation = (...args) => fetch(...args) }: { headUrl?: string; fetch?: typeof fetch } = {}
): ParquetByteSource => {
  let cachedSize: number | undefined;

  const requestRange = async (start: number, end: number, signal?: AbortSignal) => {
    throwIfAborted(signal);
    const response = await fetchImplementation(url, { headers: { Range: `bytes=${start}-${end - 1}` }, signal });
    if (response.status === 200) {
      throw new ParquetColumnRenameError(
        'range_not_supported',
        'The file server does not support range requests, so the file was not downloaded'
      );
    }
    if (response.status !== 206) {
      throw new ParquetColumnRenameError(
        'source_unavailable',
        `The Parquet file could not be read (HTTP ${response.status})`
      );
    }
    return response;
  };

  return {
    getSize: async (signal) => {
      if (cachedSize === undefined) {
        throwIfAborted(signal);
        // Without a HEAD URL, a plain GET is read only up to its headers, as the viewer's reader does.
        const sizeController = new AbortController();
        const abortSizeRequest = () => sizeController.abort();
        signal?.addEventListener('abort', abortSizeRequest, { once: true });
        let response: Response;
        try {
          response = await fetchImplementation(headUrl ?? url, {
            ...(headUrl ? { method: 'HEAD' } : {}),
            signal: sizeController.signal,
          });
        } finally {
          signal?.removeEventListener('abort', abortSizeRequest);
        }
        if (!headUrl) {
          sizeController.abort();
        }
        if (response.status !== 200) {
          throw new ParquetColumnRenameError(
            'source_unavailable',
            `The Parquet file could not be read (HTTP ${response.status})`
          );
        }
        const total = Number(response.headers.get('Content-Length'));
        if (!Number.isSafeInteger(total) || total <= 0) {
          throw new ParquetColumnRenameError('source_unavailable', 'The Parquet file size could not be determined');
        }
        cachedSize = total;
      }
      return cachedSize;
    },
    readBytes: async (start, end, signal) => {
      const bytes = await blobToBytes(await (await requestRange(start, end, signal)).blob());
      if (bytes.length !== end - start) {
        throw new ParquetColumnRenameError('source_unavailable', 'The Parquet file range was incomplete');
      }
      return bytes;
    },
    readBlob: async (start, end, signal) => {
      const blob = await (await requestRange(start, end, signal)).blob();
      if (blob.size !== end - start) {
        throw new ParquetColumnRenameError('source_unavailable', 'The Parquet file range was incomplete');
      }
      return blob;
    },
  };
};

const ascii = (bytes: Uint8Array) => String.fromCharCode(...bytes);

const readFooter = async (source: ParquetByteSource, signal?: AbortSignal) => {
  const size = await source.getSize(signal);
  if (size < MAGIC.length * 2 + 4) {
    throw new ParquetColumnRenameError('not_parquet', 'The file is too small to be a Parquet file');
  }
  const trailer = await source.readBytes(size - TRAILER_BYTES, size, signal);
  const trailerMagic = ascii(trailer.slice(4));
  if (trailerMagic === ENCRYPTED_MAGIC) {
    throw new ParquetColumnRenameError('encrypted_footer_unsupported', 'Encrypted Parquet files are not supported');
  }
  const footerLength = new DataView(trailer.buffer, trailer.byteOffset, 4).getUint32(0, true);
  if (trailerMagic !== MAGIC || footerLength === 0 || footerLength > size - TRAILER_BYTES - MAGIC.length) {
    throw new ParquetColumnRenameError('not_parquet', 'The file is not a Parquet file');
  }
  const footerStart = size - TRAILER_BYTES - footerLength;
  return { footerStart, footerBytes: await source.readBytes(footerStart, size - TRAILER_BYTES, signal) };
};

export const getParquetColumnNames = async (source: ParquetByteSource, signal?: AbortSignal) => {
  const { footerStart, footerBytes } = await readFooter(source, signal);
  return { footerStart, byteLength: await source.getSize(signal), columnNames: getFooterColumnNames(footerBytes) };
};

/**
 * Renames the columns of a flat Parquet file by copying its data bytes unchanged and writing a patched
 * footer. No values are decoded, so the result keeps the source codec, row-group layout, types and
 * statistics. Resolves with a Blob and never produces output when the mapping is incomplete.
 */
export const rewriteParquetColumnNames = async (
  source: ParquetByteSource,
  renames: ParquetColumnRename[],
  signal?: AbortSignal
): Promise<Blob> => {
  const { footerStart, footerBytes } = await readFooter(source, signal);
  const patchedFooter = renameFooterColumns(footerBytes, renames);
  const head = await source.readBytes(0, MAGIC.length, signal);
  if (ascii(head) !== MAGIC) {
    throw new ParquetColumnRenameError('not_parquet', 'The file is not a Parquet file');
  }
  const data = await source.readBlob(0, footerStart, signal);
  throwIfAborted(signal);
  const footerLength = new Uint8Array(4);
  new DataView(footerLength.buffer).setUint32(0, patchedFooter.length, true);
  return new Blob([data, patchedFooter, footerLength, MAGIC], { type: 'application/octet-stream' });
};
